/**
 * Rax Learning Ledger Component
 *
 * Displays all learned preferences and corrections with full CRUD capabilities.
 * Provides transparency into what the agent has learned about the user.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { raxApi } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

interface LedgerEntry {
  id: string;
  userId: string;
  type:
    | "personal_info"
    | "preference"
    | "vocabulary"
    | "workflow"
    | "correction"
    | "relationship"
    | "schedule"
    | "tool_preference"
    | "custom";
  key: string;
  value: string;
  description?: string;
  learnedFrom: "user_stated" | "user_corrected" | "inferred" | "imported" | "admin_set";
  sourceMessageId?: string;
  sourceMessagePreview?: string;
  confidence: number;
  useCount: number;
  createdAt: Date;
  updatedAt: Date;
  lastUsedAt?: Date;
  archived: boolean;
  tags?: string[];
}

interface LedgerStats {
  totalEntries: number;
  byType: Record<string, number>;
  archivedCount: number;
  recentlyUsed: number;
  avgConfidence: number;
}

@customElement("rax-learning-ledger")
export class RaxLearningLedger extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .ledger-container {
        padding: var(--space-6);
      }
      
      .ledger-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-5);
      }
      
      .ledger-title {
        font-size: var(--font-lg);
        font-weight: 700;
        color: var(--text-primary);
      }
      
      .header-actions {
        display: flex;
        gap: var(--space-2);
      }
      
      .action-btn {
        padding: var(--space-2) var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        font-size: var(--font-xs);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .action-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      /* Stats bar */
      .stats-bar {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: var(--space-4);
        margin-bottom: var(--space-5);
      }
      
      .stat-card {
        padding: var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        text-align: center;
      }
      
      .stat-value {
        font-size: var(--font-2xl);
        font-weight: 700;
        color: var(--rax-yellow);
      }
      
      .stat-label {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-top: var(--space-1);
      }
      
      /* Filter and search */
      .controls {
        display: flex;
        gap: var(--space-3);
        margin-bottom: var(--space-4);
        flex-wrap: wrap;
        align-items: center;
      }
      
      .search-box {
        flex: 1;
        min-width: 250px;
        position: relative;
      }
      
      .search-input {
        width: 100%;
        padding: var(--space-3) var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-primary);
        font-size: var(--font-sm);
        outline: none;
        transition: border-color var(--transition-fast);
      }
      
      .search-input:focus {
        border-color: var(--rax-yellow);
      }
      
      .search-input::placeholder {
        color: var(--text-tertiary);
      }
      
      .filter-bar {
        display: flex;
        gap: var(--space-2);
        flex-wrap: wrap;
      }
      
      .filter-chip {
        padding: var(--space-1) var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-full);
        color: var(--text-secondary);
        font-size: var(--font-xs);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .filter-chip:hover {
        border-color: var(--text-tertiary);
      }
      
      .filter-chip.active {
        background: var(--rax-yellow);
        color: var(--rax-black);
        border-color: var(--rax-yellow);
        font-weight: 600;
      }
      
      /* Entries list */
      .entries-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .entry {
        display: grid;
        grid-template-columns: auto 1fr auto auto auto;
        gap: var(--space-3);
        padding: var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        align-items: start;
        font-size: var(--font-sm);
        transition: all var(--transition-fast);
      }
      
      .entry:hover {
        border-color: var(--text-tertiary);
      }
      
      .entry.archived {
        opacity: 0.6;
        background: var(--bg-subtle);
      }
      
      .entry-icon {
        font-size: 20px;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--bg-primary);
        border-radius: var(--radius-md);
      }
      
      .entry-content {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }
      
      .entry-key {
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .entry-value {
        color: var(--text-secondary);
        word-wrap: break-word;
      }
      
      .entry-meta {
        display: flex;
        gap: var(--space-2);
        align-items: center;
        margin-top: var(--space-1);
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      .confidence-badge {
        padding: 2px 6px;
        border-radius: var(--radius-sm);
        font-size: var(--font-xs);
        font-weight: 600;
      }
      
      .confidence-high {
        background: rgba(16, 185, 129, 0.2);
        color: var(--success);
      }
      
      .confidence-medium {
        background: rgba(245, 158, 11, 0.2);
        color: var(--warning);
      }
      
      .confidence-low {
        background: rgba(239, 68, 68, 0.2);
        color: var(--error);
      }
      
      .entry-date {
        font-family: ui-monospace, monospace;
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        text-align: center;
      }
      
      .entry-actions {
        display: flex;
        gap: var(--space-1);
        opacity: 0;
        transition: opacity var(--transition-fast);
      }
      
      .entry:hover .entry-actions {
        opacity: 1;
      }
      
      .entry-action-btn {
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--bg-primary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-sm);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: 12px;
      }
      
      .entry-action-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      .entry-action-btn.danger:hover {
        border-color: var(--error);
        color: var(--error);
      }
      
      .empty-state {
        text-align: center;
        padding: var(--space-8);
        color: var(--text-tertiary);
      }

      .loading {
        text-align: center;
        padding: var(--space-8);
        color: var(--text-tertiary);
      }

      /* Source explainability */
      .source-info {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
      }

      .source-trigger {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        font-size: 10px;
        border-radius: 50%;
        background: var(--bg-primary);
        border: 1px solid var(--border-color);
        color: var(--text-tertiary);
        cursor: help;
        transition: all var(--transition-fast);
      }

      .source-trigger:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }

      .source-tooltip {
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%);
        min-width: 250px;
        max-width: 350px;
        padding: var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        z-index: 100;
        opacity: 0;
        visibility: hidden;
        transition: all var(--transition-fast);
      }

      .source-info:hover .source-tooltip {
        opacity: 1;
        visibility: visible;
      }

      .source-tooltip-label {
        font-size: var(--font-xs);
        font-weight: 600;
        color: var(--text-tertiary);
        margin-bottom: var(--space-1);
      }

      .source-tooltip-text {
        font-size: var(--font-sm);
        color: var(--text-primary);
        font-style: italic;
        word-wrap: break-word;
      }

      .source-copy-btn {
        margin-top: var(--space-2);
        padding: var(--space-1) var(--space-2);
        font-size: var(--font-xs);
        background: var(--bg-primary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-sm);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .source-copy-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }

      /* Edit modal */
      .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.7);
        z-index: 1000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--space-4);
      }
      
      .modal {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        padding: var(--space-6);
        max-width: 500px;
        width: 100%;
        max-height: 80vh;
        overflow-y: auto;
      }
      
      .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-5);
      }
      
      .modal-title {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .close-btn {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        color: var(--text-secondary);
        cursor: pointer;
        border-radius: var(--radius-sm);
        transition: all var(--transition-fast);
      }
      
      .close-btn:hover {
        background: var(--bg-hover);
        color: var(--text-primary);
      }
      
      .form-group {
        margin-bottom: var(--space-4);
      }
      
      .form-label {
        display: block;
        font-size: var(--font-sm);
        font-weight: 500;
        color: var(--text-primary);
        margin-bottom: var(--space-2);
      }
      
      .form-input,
      .form-textarea {
        width: 100%;
        padding: var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-primary);
        font-size: var(--font-sm);
        outline: none;
        transition: border-color var(--transition-fast);
        box-sizing: border-box;
      }
      
      .form-textarea {
        resize: vertical;
        min-height: 80px;
      }
      
      .form-input:focus,
      .form-textarea:focus {
        border-color: var(--rax-yellow);
      }
      
      .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-3);
        margin-top: var(--space-5);
      }
      
      .btn {
        padding: var(--space-3) var(--space-4);
        border: none;
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
        font-weight: 600;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .btn-primary {
        background: var(--rax-yellow);
        color: var(--rax-black);
      }
      
      .btn-primary:hover {
        background: var(--rax-gold);
      }
      
      .btn-secondary {
        background: var(--bg-elevated);
        color: var(--text-secondary);
        border: 1px solid var(--border-color);
      }
      
      .btn-secondary:hover {
        border-color: var(--text-tertiary);
        color: var(--text-primary);
      }
    `,
  ];

  @state()
  private entries: LedgerEntry[] = [];

  @state()
  private stats: LedgerStats | null = null;

  @state()
  private loading = true;

  @state()
  private searchQuery = "";

  @state()
  private typeFilter: string | null = null;

  @state()
  private showArchived = false;

  @state()
  private editingEntry: LedgerEntry | null = null;

  @state()
  private editFormData = {
    value: "",
    description: "",
    confidence: 0.8,
    tags: "",
  };

  override async connectedCallback() {
    super.connectedCallback();
    await this.loadEntries();
    await this.loadStats();
  }

  private async loadEntries() {
    this.loading = true;
    try {
      const res = await raxApi.request<LedgerEntry[]>(
        "GET",
        `/api/rax/ledger?includeArchived=${this.showArchived}`,
      );
      if (res.success && res.data) {
        this.entries = res.data.map((entry) => ({
          ...entry,
          createdAt: new Date(entry.createdAt),
          updatedAt: new Date(entry.updatedAt),
          lastUsedAt: entry.lastUsedAt ? new Date(entry.lastUsedAt) : undefined,
        }));
      }
    } catch (err) {
      console.error("[LearningLedger] Failed to load entries:", err);
    } finally {
      this.loading = false;
    }
  }

  private async loadStats() {
    try {
      const res = await raxApi.request<LedgerStats>("GET", "/api/rax/ledger/stats");
      if (res.success && res.data) {
        this.stats = res.data;
      }
    } catch (err) {
      console.error("[LearningLedger] Failed to load stats:", err);
    }
  }

  private get filteredEntries(): LedgerEntry[] {
    let filtered = this.entries;

    // Type filter
    if (this.typeFilter) {
      filtered = filtered.filter((e) => e.type === this.typeFilter);
    }

    // Search filter
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.key.toLowerCase().includes(query) ||
          e.value.toLowerCase().includes(query) ||
          (e.description && e.description.toLowerCase().includes(query)) ||
          (e.tags && e.tags.some((tag) => tag.toLowerCase().includes(query))),
      );
    }

    // Sort by most recent first
    return filtered.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
  }

  private getEntryIcon(type: string): string {
    const icons: Record<string, string> = {
      personal_info: "👤",
      preference: "⚙️",
      vocabulary: "📝",
      workflow: "🔄",
      correction: "✏️",
      relationship: "👥",
      schedule: "📅",
      tool_preference: "🔧",
      custom: "📋",
    };
    return icons[type] || "📋";
  }

  private formatDate(date: Date): string {
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  private getConfidenceClass(confidence: number): string {
    if (confidence >= 0.8) return "confidence-high";
    if (confidence >= 0.5) return "confidence-medium";
    return "confidence-low";
  }

  private handleSearch(e: Event) {
    this.searchQuery = (e.target as HTMLInputElement).value;
  }

  private setTypeFilter(type: string | null) {
    this.typeFilter = this.typeFilter === type ? null : type;
  }

  private async toggleShowArchived() {
    this.showArchived = !this.showArchived;
    await this.loadEntries();
  }

  private async exportLedger() {
    try {
      const response = await fetch("/api/rax/ledger/export", {
        headers: { Authorization: `Bearer ${raxApi.getToken()}` },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `rax-ledger-${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("[LearningLedger] Export failed:", err);
    }
  }

  private openEditModal(entry: LedgerEntry) {
    this.editingEntry = entry;
    this.editFormData = {
      value: entry.value,
      description: entry.description || "",
      confidence: entry.confidence,
      tags: entry.tags?.join(", ") || "",
    };
  }

  private closeEditModal() {
    this.editingEntry = null;
  }

  private handleFormChange(field: keyof typeof this.editFormData, value: string | number) {
    this.editFormData = { ...this.editFormData, [field]: value };
  }

  private async saveEntry() {
    if (!this.editingEntry) return;

    try {
      const tags = this.editFormData.tags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t);

      const res = await raxApi.request("PUT", `/api/rax/ledger/${this.editingEntry.id}`, {
        value: this.editFormData.value,
        description: this.editFormData.description || undefined,
        confidence: this.editFormData.confidence,
        tags: tags.length > 0 ? tags : undefined,
      });

      if (res.success) {
        await this.loadEntries();
        this.closeEditModal();
      }
    } catch (err) {
      console.error("[LearningLedger] Save failed:", err);
    }
  }

  private async deleteEntry(entry: LedgerEntry) {
    if (!confirm(`Delete "${entry.key}"? This cannot be undone.`)) return;

    try {
      const res = await raxApi.request("DELETE", `/api/rax/ledger/${entry.id}`);
      if (res.success) {
        await this.loadEntries();
        await this.loadStats();
      }
    } catch (err) {
      console.error("[LearningLedger] Delete failed:", err);
    }
  }

  private async archiveEntry(entry: LedgerEntry) {
    try {
      const res = await raxApi.request("POST", `/api/rax/ledger/${entry.id}/archive`);
      if (res.success) {
        await this.loadEntries();
        await this.loadStats();
      }
    } catch (err) {
      console.error("[LearningLedger] Archive failed:", err);
    }
  }

  override render() {
    if (this.loading) {
      return html`
        <div class="loading">Loading your learned preferences...</div>
      `;
    }

    const entryTypes = [...new Set(this.entries.map((e) => e.type))];

    return html`
      <div class="ledger-container">
        <div class="ledger-header">
          <div class="ledger-title">🧠 Learning Ledger</div>
          <div class="header-actions">
            <button class="action-btn" @click=${this.toggleShowArchived}>
              ${this.showArchived ? "👁️" : "📦"} ${this.showArchived ? "Hide" : "Show"} Archived
            </button>
            <button class="action-btn" @click=${this.exportLedger}>📥 Export</button>
            <button class="action-btn" @click=${this.loadEntries}>🔄 Refresh</button>
          </div>
        </div>

        ${
          this.stats
            ? html`
          <div class="stats-bar">
            <div class="stat-card">
              <div class="stat-value">${this.stats.totalEntries}</div>
              <div class="stat-label">Total Learned</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">${this.stats.archivedCount}</div>
              <div class="stat-label">Archived</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">${Math.round(this.stats.avgConfidence * 100)}%</div>
              <div class="stat-label">Avg Confidence</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">${Object.keys(this.stats.byType).length}</div>
              <div class="stat-label">Categories</div>
            </div>
          </div>
        `
            : ""
        }

        <div class="controls">
          <div class="search-box">
            <input
              class="search-input"
              type="text"
              placeholder="Search learned preferences..."
              .value=${this.searchQuery}
              @input=${this.handleSearch}
            />
          </div>

          <div class="filter-bar">
            <span
              class="filter-chip ${!this.typeFilter ? "active" : ""}"
              @click=${() => this.setTypeFilter(null)}
            >All</span>
            ${entryTypes.slice(0, 8).map(
              (type) => html`
              <span
                class="filter-chip ${this.typeFilter === type ? "active" : ""}"
                @click=${() => this.setTypeFilter(type)}
              >${type.replace("_", " ")}</span>
            `,
            )}
          </div>
        </div>

        ${
          this.filteredEntries.length > 0
            ? html`
          <div class="entries-list">
            ${this.filteredEntries.map(
              (entry) => html`
              <div class="entry ${entry.archived ? "archived" : ""}">
                <div class="entry-icon">${this.getEntryIcon(entry.type)}</div>
                <div class="entry-content">
                  <div class="entry-key">${entry.key}</div>
                  <div class="entry-value">${entry.value}</div>
                  ${
                    entry.description
                      ? html`
                    <div class="entry-meta">
                      <span>📝 ${entry.description}</span>
                    </div>
                  `
                      : ""
                  }
                  <div class="entry-meta">
                    <span class="confidence-badge ${this.getConfidenceClass(entry.confidence)}">
                      ${Math.round(entry.confidence * 100)}% confidence
                    </span>
                    <span>📍 ${entry.learnedFrom.replace("_", " ")}</span>
                    ${
                      entry.sourceMessagePreview
                        ? html`
                      <span class="source-info">
                        <span class="source-trigger" title="View source message">?</span>
                        <div class="source-tooltip">
                          <div class="source-tooltip-label">Learned from:</div>
                          <div class="source-tooltip-text">"${entry.sourceMessagePreview}"</div>
                          <button
                            class="source-copy-btn"
                            @click=${(e: Event) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(entry.sourceMessagePreview || "");
                            }}
                          >Copy</button>
                        </div>
                      </span>
                    `
                        : ""
                    }
                    ${
                      entry.tags && entry.tags.length > 0
                        ? html`
                      <span>🏷️ ${entry.tags.join(", ")}</span>
                    `
                        : ""
                    }
                  </div>
                </div>
                <div class="entry-date">${this.formatDate(entry.updatedAt)}</div>
                <div class="entry-actions">
                  <button
                    class="entry-action-btn"
                    title="Edit"
                    @click=${() => this.openEditModal(entry)}
                  >✏️</button>
                  ${
                    !entry.archived
                      ? html`
                    <button
                      class="entry-action-btn"
                      title="Archive"
                      @click=${() => this.archiveEntry(entry)}
                    >📦</button>
                  `
                      : ""
                  }
                  <button
                    class="entry-action-btn danger"
                    title="Delete"
                    @click=${() => this.deleteEntry(entry)}
                  >🗑️</button>
                </div>
              </div>
            `,
            )}
          </div>
        `
            : html`
                <div class="empty-state">
                  <p>No learned preferences yet.</p>
                  <p>Start chatting with your agent and it will learn your preferences over time.</p>
                </div>
              `
        }
      </div>

      ${
        this.editingEntry
          ? html`
        <div class="modal-backdrop" @click=${(e: Event) => e.target === e.currentTarget && this.closeEditModal()}>
          <div class="modal">
            <div class="modal-header">
              <div class="modal-title">Edit Learning Entry</div>
              <button class="close-btn" @click=${this.closeEditModal}>✕</button>
            </div>
            
            <div class="form-group">
              <label class="form-label">Key</label>
              <input
                class="form-input"
                type="text"
                .value=${this.editingEntry.key}
                readonly
              />
            </div>

            <div class="form-group">
              <label class="form-label">Value</label>
              <textarea
                class="form-textarea"
                .value=${this.editFormData.value}
                @input=${(e: Event) => this.handleFormChange("value", (e.target as HTMLTextAreaElement).value)}
              ></textarea>
            </div>

            <div class="form-group">
              <label class="form-label">Description</label>
              <textarea
                class="form-textarea"
                .value=${this.editFormData.description}
                @input=${(e: Event) => this.handleFormChange("description", (e.target as HTMLTextAreaElement).value)}
              ></textarea>
            </div>

            <div class="form-group">
              <label class="form-label">Confidence (${Math.round(this.editFormData.confidence * 100)}%)</label>
              <input
                class="form-input"
                type="range"
                min="0"
                max="1"
                step="0.1"
                .value=${String(this.editFormData.confidence)}
                @input=${(e: Event) => this.handleFormChange("confidence", Number((e.target as HTMLInputElement).value))}
              />
            </div>

            <div class="form-group">
              <label class="form-label">Tags (comma-separated)</label>
              <input
                class="form-input"
                type="text"
                .value=${this.editFormData.tags}
                @input=${(e: Event) => this.handleFormChange("tags", (e.target as HTMLInputElement).value)}
              />
            </div>

            <div class="modal-actions">
              <button class="btn btn-secondary" @click=${this.closeEditModal}>Cancel</button>
              <button class="btn btn-primary" @click=${this.saveEntry}>Save Changes</button>
            </div>
          </div>
        </div>
      `
          : ""
      }
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-learning-ledger": RaxLearningLedger;
  }
}
